import React, { useEffect, useRef, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { createGlobalStyle } from 'styled-components'
import Header from './header'
import ContactForm from './contactform'
import Footer from './footer'
import { useAccessibility } from '../context/AccessibilityContext';
import ModuleAccessibility from './moduleAccessibility'
import { usePrefersReducedMotion } from '../hook/usePrefersReducedMotion'

/* Styles globaux — accessibilité (curseurs, surbrillance) + surcharges
   globales d'interligne et d'espacement appliquées à TOUS les textes. */
const GlobalStyle = createGlobalStyle`
  body.black-large-cursor { cursor: url('arrow-pointer-solid.svg'), auto; }
  body.white-large-cursor { cursor: url('arrow-pointer-solid (1).svg'), auto; }

  .links { color: #f3effb; }
  .link-highlight { outline: 2px solid #ffe600; outline-offset: 2px; }

  /* Réglages d'accessibilité appliqués globalement (interligne / espacement).
     La taille de police, elle, est pilotée via la taille racine de <html>. */
  html.a11y-lh-md * { line-height: 1.85 !important; }
  html.a11y-lh-lg * { line-height: 2.15 !important; }
  html.a11y-ls-md * { letter-spacing: 0.06em !important; }
  html.a11y-ls-lg * { letter-spacing: 0.12em !important; }

  /* « Figer les animations » : stoppe toutes les animations/transitions CSS */
  html.a11y-stop *,
  html.a11y-stop *::before,
  html.a11y-stop *::after {
    animation-play-state: paused !important;
    transition: none !important;
  }

  /* Le module d'accessibilité (.a11y-immune) n'est PAS affecté par les réglages
     qu'il pilote : il reste compact et lisible quels que soient les choix de
     l'utilisateur. Sélecteurs volontairement plus spécifiques que les règles
     globales ci-dessus pour les emporter. */
  html.a11y-lh-md .a11y-immune, html.a11y-lh-md .a11y-immune *,
  html.a11y-lh-lg .a11y-immune, html.a11y-lh-lg .a11y-immune * {
    line-height: normal !important;
  }
  html.a11y-ls-md .a11y-immune, html.a11y-ls-md .a11y-immune *,
  html.a11y-ls-lg .a11y-immune, html.a11y-ls-lg .a11y-immune * {
    letter-spacing: normal !important;
  }
  html.a11y-stop .a11y-immune,
  html.a11y-stop .a11y-immune *,
  html.a11y-stop .a11y-immune *::before,
  html.a11y-stop .a11y-immune *::after {
    animation-play-state: running !important;
  }

  /* Lien d'évitement (accessibilité clavier) */
  .skip-link {
    position: fixed;
    top: -100%;
    left: 1rem;
    z-index: 9999;
    background: #13062c;
    color: #fff;
    padding: 10px 18px;
    border-radius: 10px;
    border: 2px solid #b06bff;
    text-decoration: none;
    font-weight: 700;
    transition: top 0.2s ease;
  }
  .skip-link:focus { top: 1rem; }
`;

const Layout = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { fontSize, lineHeight, letterSpacing, motionPaused } = useAccessibility();
  const prefersReduced = usePrefersReducedMotion();
  const location = useLocation();
  const isFirstRender = useRef(true);

  // Changement de page (SPA) : on déplace le focus sur le contenu principal
  // pour que les lecteurs d'écran annoncent la nouvelle page (RGAA 7.4 / 12).
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    document.getElementById('main-content')?.focus({ preventScroll: true });
  }, [location.pathname]);

  // Taille : on pilote la taille de police racine -> tout le texte (rem) suit.
  useEffect(() => {
    const map = {
      'text-sm': '90%',
      'text-base': '100%',
      'text-lg': '120%',
      'text-xl': '140%',
      'text-2xl': '160%',
    };
    document.documentElement.style.fontSize = map[fontSize] || '100%';
  }, [fontSize]);

  // Interligne : classe globale sur <html>.
  useEffect(() => {
    const el = document.documentElement;
    el.classList.remove('a11y-lh-md', 'a11y-lh-lg');
    if (lineHeight === 'line-height-md') el.classList.add('a11y-lh-md');
    else if (lineHeight === 'line-height-lg') el.classList.add('a11y-lh-lg');
  }, [lineHeight]);

  // Espacement des lettres : classe globale sur <html>.
  useEffect(() => {
    const el = document.documentElement;
    el.classList.remove('a11y-ls-md', 'a11y-ls-lg');
    if (letterSpacing === 'letter-spacing-md') el.classList.add('a11y-ls-md');
    else if (letterSpacing === 'letter-spacing-lg') el.classList.add('a11y-ls-lg');
  }, [letterSpacing]);

  // « Figer les animations » : classe globale + pause/lecture des vidéos.
  useEffect(() => {
    document.documentElement.classList.toggle('a11y-stop', motionPaused);
    document.querySelectorAll('video').forEach((v) => {
      if (motionPaused) v.pause();
      else if (!prefersReduced) v.play().catch(() => {});
    });
  }, [motionPaused, prefersReduced]);

  // Ouverture du formulaire de contact depuis n'importe quel composant
  useEffect(() => {
    const open = () => setIsModalOpen(true);
    window.addEventListener('pdv:open-contact', open);
    return () => window.removeEventListener('pdv:open-contact', open);
  }, []);

  return (
    <React.Fragment>
      <GlobalStyle />
      <a href="#main-content" className="skip-link">Aller au contenu principal</a>

      {/* Base atmosphérique globale derrière toutes les pages */}
      <div
        aria-hidden="true"
        className="fixed inset-0 -z-50"
        style={{
          background:
            'radial-gradient(120% 90% at 50% -10%, #1d0b45 0%, #13062c 45%, #0a0218 100%)',
        }}
      />
      <div className="pdv-grain" aria-hidden="true" />

      <ContactForm isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />
      <Header isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />
      <main id="main-content" tabIndex={-1} className="relative">
        {children}
        <Outlet />
      </main>
      <ModuleAccessibility />
      <Footer setIsModalOpen={setIsModalOpen} />
    </React.Fragment>
  )
}

export default Layout
